import { inject, Injectable } from '@angular/core';
import {
  type CellValue as ExcelJsCellValue,
  type Workbook,
  type Worksheet,
} from 'exceljs';

import { REPORT_THEME } from '../../application/excel-report.tokens';
import {
  isFormSheet,
  type FormCell,
  type FormImage,
  type FormPageMargins,
  type FormPageSetup,
  type FormSheetConfig,
} from '../../domain/form-config.types';
import {
  isFormulaCellValue,
  type AnySheetConfig,
  type CellValue,
  type ColumnDefinition,
  type PreHeaderRow,
  type PreHeaderSegment,
  type ReportConfig,
  type SheetConfig,
} from '../../domain/report-config.types';
import type {
  CellStyle,
  ConditionalFormatRule,
} from '../../domain/report-style.types';
import { ReportEngineError, type ExcelEngine } from '../excel-engine.port';
import {
  applyCellStyle,
  styleToExcelJsStyle,
} from './exceljs-style.mapper';

const DEFAULT_HEADER_ROW = 1;
const ASCII_UPPERCASE_A = 65;
const ALPHABET_LENGTH = 26;

type ExcelJsModule = typeof import('exceljs');

/**
 * exceljs no tiene build ESM: el application builder (esbuild) lo
 * deja en su propio chunk y el `import()` dinámico solo expone
 * `default` (= module.exports), sin named exports. Vite y webpack sí
 * los sintetizan, por eso se normaliza para soportar las dos formas.
 */
async function loadExcelJs(): Promise<ExcelJsModule> {
  const mod: ExcelJsModule & { default?: ExcelJsModule } =
    await import('exceljs');
  return mod.default ?? mod;
}

@Injectable({ providedIn: 'root' })
export class ExcelJsEngine implements ExcelEngine {
  private readonly theme = inject(REPORT_THEME);

  async generate(config: ReportConfig): Promise<ArrayBuffer> {
    try {
      const ExcelJS = await loadExcelJs();
      const workbook = new ExcelJS.Workbook();
      workbook.creator = config.author ?? this.theme.defaultAuthor;
      workbook.created = new Date();
      if (config.title) workbook.title = config.title;

      for (const sheetCfg of config.sheets) {
        if (isFormSheet(sheetCfg)) {
          this.buildFormSheet(workbook, sheetCfg);
        } else {
          this.buildSheet(workbook, sheetCfg);
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      return buffer as ArrayBuffer;
    } catch (err) {
      throw new ReportEngineError('Falló la generación del Excel', err);
    }
  }

  private buildSheet(
    workbook: Workbook,
    cfg: AnySheetConfig,
  ): void {
    const preHeaderCount = cfg.preHeaderRows?.length ?? 0;
    const headerRowIndex = preHeaderCount + DEFAULT_HEADER_ROW;
    const views = cfg.freezeHeader !== false
      ? [{ state: 'frozen' as const, ySplit: headerRowIndex }]
      : undefined;

    const sheet = workbook.addWorksheet(cfg.name, { views });
    const hasPreHeader = preHeaderCount > 0;

    this.setupColumns(sheet, cfg, hasPreHeader);
    if (cfg.preHeaderRows) {
      this.writePreHeaderRows(sheet, cfg.preHeaderRows);
    }
    this.writeHeader(sheet, cfg, headerRowIndex);
    this.styleHeader(sheet, cfg, headerRowIndex);
    this.writeRows(sheet, cfg, headerRowIndex);
    this.applyAutoFilter(sheet, cfg, headerRowIndex);
    this.applyConditionalFormats(sheet, cfg);
  }

  private buildFormSheet(
    workbook: Workbook,
    cfg: FormSheetConfig,
  ): void {
    const sheet = workbook.addWorksheet(cfg.name, {
      views: [{
        showGridLines: cfg.showGridLines ?? false,
        zoomScale: cfg.zoomScale,
      }],
    });

    cfg.columnWidths?.forEach((width, idx) => {
      sheet.getColumn(idx + 1).width = width;
    });
    cfg.cells.forEach((cell) => this.writeFormCell(sheet, cell));
    Object.entries(cfg.rowHeights ?? {}).forEach(([row, height]) => {
      sheet.getRow(Number(row)).height = height;
    });
    cfg.images?.forEach((img) => this.addFormImage(workbook, sheet, img));
    if (cfg.pageSetup) this.applyPageSetup(sheet, cfg.pageSetup);
  }

  // Configura la impresión de la hoja. `scale` y el ajuste a página
  // son EXCLUYENTES en Excel: si el caller pide fitToWidth/Height se
  // activa `fitToPage` y se omite la escala manual para que el ajuste
  // mande. Sin esto, un formato ancho se parte en varias páginas al
  // exportar a PDF.
  private applyPageSetup(sheet: Worksheet, ps: FormPageSetup): void {
    const fitToPage =
      ps.fitToWidth !== undefined || ps.fitToHeight !== undefined;
    sheet.pageSetup = {
      ...sheet.pageSetup,
      ...(ps.orientation ? { orientation: ps.orientation } : {}),
      fitToPage,
      ...(fitToPage
        ? { fitToWidth: ps.fitToWidth, fitToHeight: ps.fitToHeight }
        : ps.scale !== undefined
          ? { scale: ps.scale }
          : {}),
      ...(ps.margins ? { margins: this.toMargins(ps.margins) } : {}),
      ...(ps.printArea ? { printArea: ps.printArea } : {}),
    };
  }

  // Los @types de exceljs exigen los 6 márgenes; el dominio los deja
  // opcionales. Se completan los faltantes con los defaults de Excel.
  private toMargins(
    m: FormPageMargins,
  ): NonNullable<Worksheet['pageSetup']['margins']> {
    return {
      left: m.left ?? 0.7,
      right: m.right ?? 0.7,
      top: m.top ?? 0.75,
      bottom: m.bottom ?? 0.75,
      header: m.header ?? 0.3,
      footer: m.footer ?? 0.3,
    };
  }

  // Embebe una imagen (logo) en la hoja. El base64 se registra una
  // vez a nivel workbook (`addImage`) y se ancla a la hoja por
  // top-left + extensión en píxeles (`ext`), lo que respeta la
  // proporción nativa del archivo en vez de estirarlo a un rango.
  private addFormImage(
    workbook: Workbook,
    sheet: Worksheet,
    img: FormImage,
  ): void {
    const imageId = workbook.addImage({
      base64: img.base64,
      extension: img.extension,
    });
    sheet.addImage(imageId, {
      tl: { col: img.tl.col, row: img.tl.row },
      ext: { width: img.ext.width, height: img.ext.height },
    });
  }

  private writeFormCell(sheet: Worksheet, cell: FormCell): void {
    if (cell.merge) sheet.mergeCells(cell.merge);

    const target = sheet.getCell(cell.ref);
    // Una fórmula viva (`{ formula }`) tiene prioridad sobre el valor
    // literal: Excel la recalcula al abrir (ej. =TODAY()). Sin fórmula,
    // se escribe el valor crudo (undefined → null para vaciar la celda).
    target.value = cell.formula
      ? ({ formula: cell.formula } as ExcelJsCellValue)
      : ((cell.value ?? null) as ExcelJsCellValue);
    if (cell.numFmt) target.numFmt = cell.numFmt;

    if (cell.dataValidation) {
      // Lista inline: las opciones van entre comillas separadas por coma.
      target.dataValidation = {
        type: 'list',
        allowBlank: cell.dataValidation.allowBlank ?? true,
        formulae: [`"${cell.dataValidation.values.join(',')}"`],
      };
    }

    if (cell.style) {
      this.styleRange(sheet, cell.merge ?? cell.ref, cell.style);
    }
  }

  // Estiliza TODAS las celdas del rango: en un merge, ExcelJS dibuja
  // los bordes de las celdas subyacentes, así que aplicar el estilo
  // solo al ancla dejaría el contorno de la caja incompleto.
  private styleRange(
    sheet: Worksheet,
    range: string,
    style: Partial<CellStyle>,
  ): void {
    const [start, end = start] = range.split(':');
    const from = this.parseRef(start);
    const to = this.parseRef(end);

    for (let row = from.row; row <= to.row; row++) {
      for (let col = from.col; col <= to.col; col++) {
        applyCellStyle(sheet.getRow(row).getCell(col), style);
      }
    }
  }

  private parseRef(ref: string): { row: number; col: number } {
    const match = /^([A-Z]+)(\d+)$/.exec(ref.toUpperCase());
    if (!match) {
      throw new ReportEngineError(
        `Referencia de celda inválida: ${ref}`,
      );
    }

    let col = 0;
    for (const letter of match[1]) {
      col =
        col * ALPHABET_LENGTH +
        (letter.charCodeAt(0) - ASCII_UPPERCASE_A + 1);
    }
    return { row: Number(match[2]), col };
  }

  private setupColumns<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
    skipHeader: boolean,
  ): void {
    sheet.columns = cfg.columns.map((c) => ({
      header: skipHeader || c.spacer ? undefined : c.header,
      key: c.key,
      width: c.width ?? this.theme.defaultColumnWidth,
      hidden: c.hidden ?? false,
      style: {
        numFmt: c.numFmt,
        alignment: c.align ? { horizontal: c.align } : undefined,
      },
    }));
  }

  private writePreHeaderRows(
    sheet: Worksheet,
    preHeaderRows: ReadonlyArray<PreHeaderRow>,
  ): void {
    preHeaderRows.forEach((row, idx) => {
      const rowIndex = idx + 1;
      const r = sheet.getRow(rowIndex);

      row.values.forEach((val, colIdx) => {
        r.getCell(colIdx + 1).value = (val ?? null) as never;
      });

      if (row.height !== undefined) r.height = row.height;

      if (row.style) {
        const style = row.style;
        const cellCount = Math.max(row.values.length, 1);
        for (let i = 1; i <= cellCount; i++) {
          applyCellStyle(r.getCell(i), style);
        }
      }

      if (row.merge) sheet.mergeCells(row.merge);
      row.segments?.forEach((segment) =>
        this.writePreHeaderSegment(sheet, rowIndex, segment),
      );
    });
  }

  // Mismo orden que una form cell: merge, valor en el ancla y
  // estilo en TODO el rango (ver styleRange). Un tramo de una
  // sola celda no se combina.
  private writePreHeaderSegment(
    sheet: Worksheet,
    rowIndex: number,
    segment: PreHeaderSegment,
  ): void {
    const anchor = `${this.columnLetter(segment.fromColumn)}${rowIndex}`;
    const lastRow = rowIndex + (segment.rowSpan ?? 1) - 1;
    const end = `${this.columnLetter(segment.toColumn)}${lastRow}`;
    const range = `${anchor}:${end}`;

    if (end !== anchor) sheet.mergeCells(range);
    sheet.getCell(anchor).value = this.toExcelValue(segment.value);
    if (segment.style) this.styleRange(sheet, range, segment.style);
  }

  private writeHeader<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
    headerRowIndex: number,
  ): void {
    if (headerRowIndex === DEFAULT_HEADER_ROW) return;
    const headerRow = sheet.getRow(headerRowIndex);
    cfg.columns.forEach((col, idx) => {
      // Sin valor: styleHeader (eachCell) saltea la celda vacía.
      if (col.spacer) return;
      headerRow.getCell(idx + 1).value = col.header;
    });
  }

  private styleHeader<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
    headerRowIndex: number,
  ): void {
    const headerRow = sheet.getRow(headerRowIndex);
    const headerStyle = {
      ...this.theme.header,
      ...cfg.headerStyleOverride,
    };
    headerRow.eachCell((cell) => applyCellStyle(cell, headerStyle));
    if (this.theme.headerHeight !== undefined) {
      headerRow.height = this.theme.headerHeight;
    }
  }

  private writeRows<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
    headerRowIndex: number,
  ): void {
    cfg.rows.forEach((row, idx) => {
      const rowIndex = headerRowIndex + 1 + idx;
      const r = sheet.getRow(rowIndex);
      r.values = this.buildRowValues(cfg.columns, row);

      cfg.columns.forEach((col, colIdx) => {
        if (col.spacer || !col.cellStyle) return;
        applyCellStyle(r.getCell(colIdx + 1), col.cellStyle(row));
      });
    });
  }

  private buildRowValues<T>(
    columns: ReadonlyArray<ColumnDefinition<T>>,
    row: T,
  ): { [key: string]: ExcelJsCellValue } {
    const out: { [key: string]: ExcelJsCellValue } = {};
    for (const col of columns) {
      if (col.spacer) continue;
      const raw = col.formatter
        ? col.formatter(row)
        : (row as Record<string, unknown>)[col.key];
      out[col.key] = this.toExcelValue(raw as CellValue);
    }
    return out;
  }

  // Mapeo explícito dominio → exceljs (el port se cruza acá, no
  // con un cast). Una fórmula viaja como CellFormulaValue: Excel
  // recalcula `formula` al editar y pinta `result` al abrir;
  // `result: null` se normaliza a "sin caché" (exceljs espera
  // undefined, no null). Un escalar `undefined` baja a null para
  // vaciar la celda en vez de que el values-setter la saltee.
  private toExcelValue(raw: CellValue): ExcelJsCellValue {
    if (isFormulaCellValue(raw)) {
      return (
        raw.result == null
          ? { formula: raw.formula }
          : { formula: raw.formula, result: raw.result }
      ) as ExcelJsCellValue;
    }
    return (raw ?? null) as ExcelJsCellValue;
  }

  private applyAutoFilter<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
    headerRowIndex: number,
  ): void {
    const dataRows = cfg.rows.length - (cfg.autoFilterFooterRows ?? 0);
    if (cfg.autoFilter === false || dataRows <= 0) return;
    const lastCol = this.columnLetter(cfg.columns.length);
    const lastRow = headerRowIndex + dataRows;
    sheet.autoFilter = `A${headerRowIndex}:${lastCol}${lastRow}`;
  }

  private applyConditionalFormats<T>(
    sheet: Worksheet,
    cfg: SheetConfig<T>,
  ): void {
    cfg.conditionalFormats?.forEach((rule, i) => {
      sheet.addConditionalFormatting({
        ref: rule.range,
        rules: [this.buildConditionalRule(rule, i)],
      });
    });
  }

  private buildConditionalRule(
    rule: ConditionalFormatRule,
    index: number,
  ): Parameters<Worksheet['addConditionalFormatting']>[0]['rules'][number] {
    const built = {
      type: rule.type,
      operator: rule.operator,
      formulae: [...rule.formulae],
      priority: index + 1,
      style: styleToExcelJsStyle(rule.style),
    };
    return built as Parameters<
      Worksheet['addConditionalFormatting']
    >[0]['rules'][number];
  }

  private columnLetter(n: number): string {
    let result = '';
    let value = n;
    while (value > 0) {
      const remainder = (value - 1) % ALPHABET_LENGTH;
      result = String.fromCharCode(ASCII_UPPERCASE_A + remainder) + result;
      value = Math.floor((value - 1) / ALPHABET_LENGTH);
    }
    return result;
  }
}
